import { useCallback, useEffect, useState } from "react";
import api from "../../services/api";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL");
}

function statusLabel(status) {
  const key = String(status || "").trim().toLowerCase();
  if (key === "approved") return "Goedgekeurd";
  if (key === "rejected") return "Afgewezen";
  return "In behandeling";
}

function statusClass(status) {
  const key = String(status || "").trim().toLowerCase();
  if (key === "approved") return "bg-emerald-100 text-emerald-700";
  if (key === "rejected") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}

export default function LeaveRequestsReviewPage() {
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = statusFilter ? { status: statusFilter } : {};
      const res = await api.get("/leave-requests", { params });
      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Verlofaanvragen laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  async function decide(item, decision) {
    if (!item?._id) return;

    const note = window.prompt(
      decision === "approved"
        ? "Optionele opmerking bij goedkeuren"
        : "Waarom afwijzen? (optioneel)",
      ""
    );

    try {
      setSavingId(String(item._id));
      await api.post(`/leave-requests/${item._id}/decision`, {
        status: decision,
        note: String(note || "").trim(),
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Beslissing opslaan mislukt");
    } finally {
      setSavingId("");
    }
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Verlofaanvragen</h2>
            <p className="text-sm text-gray-500">Beoordeel aanvragen van monteurs.</p>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border px-2 py-2 text-sm"
          >
            <option value="pending">In behandeling</option>
            <option value="approved">Goedgekeurd</option>
            <option value="rejected">Afgewezen</option>
            <option value="">Alles</option>
          </select>
        </div>
      </section>

      {error ? (
        <section className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </section>
      ) : null}

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        {loading ? <p className="text-sm text-gray-500">Laden...</p> : null}

        {!loading && items.length === 0 ? (
          <p className="text-sm text-gray-500">Geen verlofaanvragen gevonden.</p>
        ) : null}

        {!loading && items.length > 0 ? (
          <div className="space-y-2">
            {items.map((item) => {
              const pending = String(item?.status || "").toLowerCase() === "pending";
              return (
                <article key={item._id} className="rounded-lg border border-gray-200 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {item?.userId?.naam || item?.userId?.email || "Monteur"}
                      </p>
                      <p className="text-sm text-gray-700">
                        {formatDate(item?.fromDate)} t/m {formatDate(item?.toDate)}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        Aangevraagd: {formatDate(item?.createdAt)}
                      </p>
                      {item?.reason ? (
                        <p className="mt-1 text-sm text-gray-700">{item.reason}</p>
                      ) : null}
                      {item?.reviewNote ? (
                        <p className="mt-1 text-xs text-gray-600">Review: {item.reviewNote}</p>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(item?.status)}`}>
                        {statusLabel(item?.status)}
                      </span>

                      {pending ? (
                        <>
                          <button
                            type="button"
                            onClick={() => decide(item, "approved")}
                            disabled={savingId === String(item._id)}
                            className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 disabled:opacity-60"
                          >
                            Goedkeuren
                          </button>
                          <button
                            type="button"
                            onClick={() => decide(item, "rejected")}
                            disabled={savingId === String(item._id)}
                            className="rounded border border-red-300 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 disabled:opacity-60"
                          >
                            Afwijzen
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </section>
    </div>
  );
}
